import { AsyncPipe, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Directive, Input, inject, ChangeDetectorRef, Pipe, NgModule, Optional, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Router } from '@angular/router';
import { map, catchError, distinctUntilChanged, shareReplay, take, switchMap as switchMap$1 } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { ReplaySubject, throwError, isObservable, of, switchMap, combineLatest, Subscription, map as map$1, from, tap, BehaviorSubject, combineLatestWith, filter, EMPTY } from 'rxjs';
import { EMPTY_SUBSCRIPTION } from 'rxjs/internal/Subscription';
import { isPromise } from 'rxjs/internal/util/isPromise';
import { HttpClientTestingModule } from '@angular/common/http/testing';

function scopePermission(domain, permission) {
    return `${domain}:${permission}`;
}

const DEFAULT_ON_FORBIDDEN_CALLBACK = new InjectionToken('default_on_forbidden_callback');
const BASE_PATH = new InjectionToken('base_path');
/*
 * Holds an observable of the default resource to check permissions for.
 * This is meant to be set by the client application, and is used by the authz* directives and pipes:
 * {@link AuthzDirective}
 * {@link AuthzPipe}
 */
class DefaultResourceProvider {
}

class AuthzNGService {
    constructor(http, basePath) {
        this.http = http;
        this.basePath = basePath;
    }
    listAuthorizedPermissionsScoped(domain, resource) {
        const body = {
            domain,
            resourceIdentifier: {
                type: resource.type,
                value: resource.id,
            },
        };
        const url = `${this.basePath}/authorization/list-authorized-permissions-scoped`;
        return this.http
            .post(url, body)
            .pipe(map(({ permissions }) => permissions.map(({ domain, name }) => scopePermission(domain, name))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthzNGService, deps: [{ token: i1.HttpClient }, { token: BASE_PATH }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthzNGService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthzNGService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [BASE_PATH]
                }] }]; } });

const getKeyResource = (resource) => `${resource.type}:${resource.id}`;
const getKeyDomain = (domain) => `(${domain})`;
const getKey = (resource, domain) => `${getKeyResource(resource)}${getKeyDomain(domain)}`;
/**
 * Memoizes permissions it loads.
 *
 * Loads permissions from an AuthorizationProvider, caches the result. To release the cache, call `refresh` for
 * a partial refresh or `teardown` for a full refresh.
 */
class CachedPermissionService {
    constructor(authzNgService) {
        this.authzNgService = authzNgService;
        this.cache = new Map();
    }
    /**
     * Get permissions for the given domain and resource.
     */
    getPermissions(resource, domain) {
        let record = this.cache.get(getKey(resource, domain));
        if (!record) {
            record = new CacheRecord(resource, domain);
            this.subscribeToPermissions(record);
            this.cache.set(getKey(resource, domain), record);
        }
        return record.permissions$.asObservable();
    }
    /**
     * Refresh all permissions for a given resource.
     * @param select
     */
    refresh(select) {
        for (const [key, record] of this.cache.entries()) {
            // by default, select all records
            // if a resource is set, only select records of that resource
            // if a domain is set, only select records of that domain
            let willRecordRefresh = true;
            willRecordRefresh &&= !select.resource || key.startsWith(getKeyResource(select.resource));
            willRecordRefresh &&= !select.domain || key.endsWith(getKeyDomain(select.domain));
            if (willRecordRefresh) {
                record.sourceSubscription.unsubscribe();
                this.subscribeToPermissions(record);
            }
        }
    }
    /**
     * Unsubscribe from all permission observables and all subscribers.
     */
    teardown() {
        for (const record of this.cache.values()) {
            record.sourceSubscription.unsubscribe();
            record.permissions$.complete();
        }
    }
    /**
     * Subscribes the cache record to the permissions observable and requests the list of permissions.
     */
    subscribeToPermissions(cacheRecord) {
        cacheRecord.sourceSubscription = this.authzNgService
            .listAuthorizedPermissionsScoped(cacheRecord.domain, cacheRecord.resource)
            .subscribe(cacheRecord.permissions$);
        return cacheRecord;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CachedPermissionService, deps: [{ token: AuthzNGService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CachedPermissionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CachedPermissionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AuthzNGService }]; } });
class CacheRecord {
    constructor(resource, domain, sourceSubscription = EMPTY_SUBSCRIPTION) {
        this.resource = resource;
        this.domain = domain;
        this.sourceSubscription = sourceSubscription;
        this.permissions$ = new ReplaySubject(1);
    }
}

/**
 * Compatibility layer for the old `ResourceAuthorization[V1]Service`.
 *
 * @deprecated use `AuthorizationService` instead
 */
class ResourceAuthorizationV2Service {
    constructor(resource, permissionService, authorizationService, authzNgService) {
        this.resource = resource;
        this.permissionService = permissionService;
        this.authorizationService = authorizationService;
        this.authzNgService = authzNgService;
    }
    getPermissionList(domain, config = { fetchPolicy: 'cache-first' }) {
        switch (config.fetchPolicy) {
            case 'no-cache':
                return this.authzNgService
                    .listAuthorizedPermissionsScoped(domain, this.resource)
                    .pipe(catchError((error) => {
                    return throwError(() => error);
                }));
            case 'network-first':
                if (!(this.permissionService instanceof CachedPermissionService)) {
                    throw new Error("Fetch policy 'network-first' requires a CachedPermissionService instance");
                }
                this.permissionService.refresh({
                    resource: this.resource,
                    domain: domain,
                });
                return this.permissionService.getPermissions(this.resource, domain);
            default:
                return this.permissionService.getPermissions(this.resource, domain);
        }
    }
    hasPermission(permission) {
        return this.authorizationService.isAllowed(permission, this.resource);
    }
    hasAnyPermission(permissions) {
        return this.authorizationService.isAllowedAny(permissions, this.resource);
    }
    hasEveryPermission(permissions) {
        return this.authorizationService.isAllowedEvery(permissions, this.resource);
    }
}

function splitPermission(permission) {
    const [domain, ...rest] = permission.split(':');
    return [domain, rest.join('')];
}

/**
 * PermissionService provides permissions for use in authorization checks.
 * AuthorizationService calls PermissionService to get a list of permissions to check against.
 * @see AuthorizationService
 */
class PermissionService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PermissionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PermissionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PermissionService, decorators: [{
            type: Injectable
        }] });

/**
 * AuthorizationService checks if a permission is granted.
 * It uses the PermissionService to fetch permissions for the provided resource.
 * @see PermissionService
 */
class AuthorizationService {
    constructor(defaultResourceProvider, permissionService) {
        this.defaultResourceProvider = defaultResourceProvider;
        this.permissionService = permissionService;
    }
    /**
     * Checks if the provided permission is granted.
     *
     * @remarks
     * 1. emits multiple times - whenever the permission updates.
     * 2. the result of isAllowed() for the same permission and resource is cached
     * and shared across all subscribers.
     * 3. to refresh permissions, flush the PermissionService cache.
     *
     * @param permission - The permission to check for.
     * @param resource - The resource to check the permission for. Default resource if missing
     * @returns Emits `true` if the permission is granted.
     */
    isAllowed(permission, resource) {
        let resource$;
        const [domain] = splitPermission(permission);
        if (resource) {
            resource$ = isObservable(resource) ? resource : of(resource);
        }
        else {
            resource$ = this.defaultResourceProvider.getDefaultResource();
        }
        return resource$.pipe(switchMap(resource => this.permissionService.getPermissions(resource, domain)), map(permissions => permissions.includes(permission)), distinctUntilChanged(), shareReplay(1));
    }
    /**
     * Checks if any of the provided `permissions` are granted.
     *
     * @remarks
     * 1. emits multiple times - whenever the permissions update.
     * 2. the result of isAllowedAny() for the same permissions and resource is cached
     * and shared across all subscribers.
     * 3. to refresh permissions, flush the PermissionService cache.
     *
     * @param permissions - The permissions to check for.
     * @param resource - The resource to check the permissions for. Default resource if missing.
     * @returns Emits `true` if any of the permissions are granted.
     */
    isAllowedAny(permissions, resource) {
        return combineLatest(permissions.map((perm) => this.isAllowed(perm, resource))).pipe(map((isAuthorizedList) => isAuthorizedList.some(Boolean)), distinctUntilChanged(), shareReplay(1));
    }
    /**
     * Checks if every permission in `permissions` is granted.
     *
     * @remarks
     * 1. emits multiple times - whenever the permissions update.
     * 2. the result of isAllowedEvery() for the same permissions and resource is cached
     * and shared across all subscribers.
     * 3. to refresh permissions, flush the PermissionService cache.
     *
     * @param permissions - The permissions to check for.
     * @param resource - The resource to check the permissions for. Default resource if missing.
     * @returns Emits `true` if all permissions are granted.
     */
    isAllowedEvery(permissions, resource) {
        return combineLatest(permissions.map((perm) => this.isAllowed(perm, resource))).pipe(map((isAuthorizedList) => isAuthorizedList.every(Boolean)), distinctUntilChanged(), shareReplay(1));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationService, deps: [{ token: DefaultResourceProvider }, { token: PermissionService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DefaultResourceProvider }, { type: PermissionService }]; } });

/**
 * @deprecated use `AuthorizationService` instead
 */
class AuthorizationServiceResolver {
    constructor(defaultResourceProvider, authorizationService, permissionService, authzNgService) {
        this.defaultResourceProvider = defaultResourceProvider;
        this.authorizationService = authorizationService;
        this.permissionService = permissionService;
        this.authzNgService = authzNgService;
        this.resourceServiceMap = new Map();
    }
    /**
     * @returns an instance of the `ResourceAuthorizationService` attached to the
     * provided resource. If the instance does not exist it will be created.
     * Any subsequent call for the same resource will return the same instance.
     */
    resolve(resource) {
        const id = `${resource.type}:${resource.id}`;
        if (!this.resourceServiceMap.has(id)) {
            this.resourceServiceMap.set(id, new ResourceAuthorizationV2Service(resource, this.permissionService, this.authorizationService, this.authzNgService));
        }
        return this.resourceServiceMap.get(id);
    }
    /**
     * @returns an `Observable` that emits the instance of the default `ResourceAuthorizationService`
     * if configured via `#forRoot` method of the `AuthorizationModule`.
     */
    getDefaultService() {
        return this.defaultResourceProvider
            .getDefaultResource()
            .pipe(map((resource) => this.resolve(resource)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationServiceResolver, deps: [{ token: DefaultResourceProvider }, { token: AuthorizationService }, { token: CachedPermissionService }, { token: AuthzNGService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationServiceResolver }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationServiceResolver, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DefaultResourceProvider }, { type: AuthorizationService }, { type: CachedPermissionService }, { type: AuthzNGService }]; } });

class CommonAuthzDirective {
    constructor() {
        this.isShownSubscription = new Subscription(); // null subscription as initial value
    }
    ngOnInit() {
        this.update();
    }
    ngOnChanges() {
        this.update();
    }
    update() {
        const isShown$ = this.getIsShown();
        this.isShownSubscription.unsubscribe();
        this.isShownSubscription = isShown$.subscribe(this.setVisibility.bind(this));
    }
    setVisibility(isShown) {
        if (isShown && !this.view.length) {
            this.view.createEmbeddedView(this.template);
        }
        else if (!isShown && this.view.length) {
            this.view.clear();
        }
    }
    getResource() {
        return (this.resource && this.resourceId)
            ? { type: this.resource, id: this.resourceId }
            : undefined;
    }
    ngOnDestroy() {
        this.isShownSubscription.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CommonAuthzDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: CommonAuthzDirective, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CommonAuthzDirective, decorators: [{
            type: Directive
        }] });

class IsAllowedDirective extends CommonAuthzDirective {
    constructor(authorizationService, view, template) {
        super();
        this.authorizationService = authorizationService;
        this.view = view;
        this.template = template;
    }
    getIsShown() {
        return this.authorizationService.isAllowed(this.permissions, this.getResource());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedDirective, deps: [{ token: AuthorizationService }, { token: i0.ViewContainerRef }, { token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: IsAllowedDirective, selector: "[isAllowed], [isAllowedResource], [isAllowedId]", inputs: { permissions: ["isAllowed", "permissions"], resource: ["isAllowedResource", "resource"], resourceId: ["isAllowedId", "resourceId"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[isAllowed], [isAllowedResource], [isAllowedId]',
                }]
        }], ctorParameters: function () { return [{ type: AuthorizationService }, { type: i0.ViewContainerRef }, { type: i0.TemplateRef }]; }, propDecorators: { permissions: [{
                type: Input,
                args: ['isAllowed']
            }], resource: [{
                type: Input,
                args: ['isAllowedResource']
            }], resourceId: [{
                type: Input,
                args: ['isAllowedId']
            }] } });

class IsAllowedAnyDirective extends CommonAuthzDirective {
    constructor(authorizationService, view, template) {
        super();
        this.authorizationService = authorizationService;
        this.view = view;
        this.template = template;
    }
    getIsShown() {
        return this.authorizationService.isAllowedAny(this.permissions, this.getResource());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedAnyDirective, deps: [{ token: AuthorizationService }, { token: i0.ViewContainerRef }, { token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: IsAllowedAnyDirective, selector: "[isAllowedAny], [isAllowedAnyResource], [isAllowedAnyId]", inputs: { permissions: ["isAllowedAny", "permissions"], resource: ["isAllowedAnyResource", "resource"], resourceId: ["isAllowedAnyId", "resourceId"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedAnyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[isAllowedAny], [isAllowedAnyResource], [isAllowedAnyId]',
                }]
        }], ctorParameters: function () { return [{ type: AuthorizationService }, { type: i0.ViewContainerRef }, { type: i0.TemplateRef }]; }, propDecorators: { permissions: [{
                type: Input,
                args: ['isAllowedAny']
            }], resource: [{
                type: Input,
                args: ['isAllowedAnyResource']
            }], resourceId: [{
                type: Input,
                args: ['isAllowedAnyId']
            }] } });

class IsAllowedEveryDirective extends CommonAuthzDirective {
    constructor(authorizationService, view, template) {
        super();
        this.authorizationService = authorizationService;
        this.view = view;
        this.template = template;
    }
    getIsShown() {
        return this.authorizationService.isAllowedEvery(this.permissions, this.getResource());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedEveryDirective, deps: [{ token: AuthorizationService }, { token: i0.ViewContainerRef }, { token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: IsAllowedEveryDirective, selector: "[isAllowedEvery], [isAllowedEveryResource], [isAllowedEveryId]", inputs: { permissions: ["isAllowedEvery", "permissions"], resource: ["isAllowedEveryResource", "resource"], resourceId: ["isAllowedEveryId", "resourceId"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedEveryDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[isAllowedEvery], [isAllowedEveryResource], [isAllowedEveryId]',
                }]
        }], ctorParameters: function () { return [{ type: AuthorizationService }, { type: i0.ViewContainerRef }, { type: i0.TemplateRef }]; }, propDecorators: { permissions: [{
                type: Input,
                args: ['isAllowedEvery']
            }], resource: [{
                type: Input,
                args: ['isAllowedEveryResource']
            }], resourceId: [{
                type: Input,
                args: ['isAllowedEveryId']
            }] } });

class IsNotAllowedDirective extends CommonAuthzDirective {
    constructor(authorizationService, view, template) {
        super();
        this.authorizationService = authorizationService;
        this.view = view;
        this.template = template;
    }
    getIsShown() {
        return this.authorizationService.isAllowed(this.permissions, this.getResource()).pipe(map$1((isAllowed) => !isAllowed));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsNotAllowedDirective, deps: [{ token: AuthorizationService }, { token: i0.ViewContainerRef }, { token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: IsNotAllowedDirective, selector: "[isNotAllowed], [isNotAllowedResource], [isNotAllowedId]", inputs: { permissions: ["isNotAllowed", "permissions"], resource: ["isNotAllowedResource", "resource"], resourceId: ["isNotAllowedId", "resourceId"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsNotAllowedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[isNotAllowed], [isNotAllowedResource], [isNotAllowedId]',
                }]
        }], ctorParameters: function () { return [{ type: AuthorizationService }, { type: i0.ViewContainerRef }, { type: i0.TemplateRef }]; }, propDecorators: { permissions: [{
                type: Input,
                args: ['isNotAllowed']
            }], resource: [{
                type: Input,
                args: ['isNotAllowedResource']
            }], resourceId: [{
                type: Input,
                args: ['isNotAllowedId']
            }] } });

class IsAllowedBasePipe {
    ngOnDestroy() {
        this.asyncPipe.ngOnDestroy();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedBasePipe, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedBasePipe }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedBasePipe, decorators: [{
            type: Injectable
        }] });

class IsAllowedImpurePipe extends IsAllowedBasePipe {
    constructor() {
        super(...arguments);
        this.asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
        this.authorizationService = inject(AuthorizationService);
    }
    transform(input, resourceType, resourceId) {
        const result$ = this.authorizationService.isAllowed(input, (resourceType && resourceId)
            ? { type: resourceType, id: resourceId }
            : undefined);
        return this.asyncPipe.transform(result$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedImpurePipe, deps: null, target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedImpurePipe, name: "isAllowedImpure", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedImpurePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'isAllowedImpure', pure: false }]
        }] });

class IsAllowedAnyImpurePipe extends IsAllowedBasePipe {
    constructor() {
        super(...arguments);
        this.asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
        this.authorizationService = inject(AuthorizationService);
    }
    transform(input, resourceType, resourceId) {
        const result$ = this.authorizationService.isAllowedAny(input, (resourceType && resourceId)
            ? { type: resourceType, id: resourceId }
            : undefined);
        return this.asyncPipe.transform(result$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedAnyImpurePipe, deps: null, target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedAnyImpurePipe, name: "isAllowedAnyImpure", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedAnyImpurePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'isAllowedAnyImpure', pure: false }]
        }] });

class IsAllowedEveryImpurePipe extends IsAllowedBasePipe {
    constructor() {
        super(...arguments);
        this.asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
        this.authorizationService = inject(AuthorizationService);
    }
    transform(input, resourceType, resourceId) {
        const result$ = this.authorizationService.isAllowedEvery(input, (resourceType && resourceId)
            ? { type: resourceType, id: resourceId }
            : undefined);
        return this.asyncPipe.transform(result$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedEveryImpurePipe, deps: null, target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedEveryImpurePipe, name: "isAllowedEveryImpure", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedEveryImpurePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'isAllowedEveryImpure', pure: false }]
        }] });

class IsNotAllowedImpurePipe extends IsAllowedBasePipe {
    constructor() {
        super(...arguments);
        this.asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
        this.authorizationService = inject(AuthorizationService);
    }
    transform(input, resourceType, resourceId) {
        const result$ = this.authorizationService.isAllowed(input, (resourceType && resourceId)
            ? { type: resourceType, id: resourceId }
            : undefined).pipe(map$1((isAllowed) => !isAllowed));
        return this.asyncPipe.transform(result$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsNotAllowedImpurePipe, deps: null, target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: IsNotAllowedImpurePipe, name: "isNotAllowedImpure", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsNotAllowedImpurePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'isNotAllowedImpure', pure: false }]
        }] });

class IsAllowedNoneImpurePipe extends IsAllowedBasePipe {
    constructor() {
        super(...arguments);
        this.asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
        this.authorizationService = inject(AuthorizationService);
    }
    transform(input, resourceType, resourceId) {
        const result$ = this.authorizationService.isAllowedAny(input, (resourceType && resourceId)
            ? { type: resourceType, id: resourceId }
            : undefined).pipe(map$1((isAllowed) => !isAllowed));
        return this.asyncPipe.transform(result$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedNoneImpurePipe, deps: null, target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedNoneImpurePipe, name: "isAllowedNoneImpure", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IsAllowedNoneImpurePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'isAllowedNoneImpure', pure: false }]
        }] });

const publicApi = [
    IsAllowedAnyImpurePipe,
    IsAllowedEveryImpurePipe,
    IsAllowedNoneImpurePipe,
    IsAllowedImpurePipe,
    IsNotAllowedImpurePipe,
    IsAllowedAnyDirective,
    IsAllowedDirective,
    IsAllowedEveryDirective,
    IsNotAllowedDirective,
];
class AuthorizerModule {
    static forRoot(config) {
        const defaultConfifg = {
            basePath: '/authz/4.0/authorizer',
            defaultOnForbiddenCallback: () => inject(Router).createUrlTree(['/']),
        };
        const finalConfig = { ...defaultConfifg, ...config };
        return {
            ngModule: AuthorizerModule,
            providers: [
                AuthorizationService,
                CachedPermissionService,
                AuthorizationServiceResolver,
                AuthzNGService,
                {
                    provide: DEFAULT_ON_FORBIDDEN_CALLBACK,
                    useValue: finalConfig.defaultOnForbiddenCallback,
                },
                {
                    provide: BASE_PATH,
                    useValue: finalConfig.basePath,
                },
                {
                    provide: DefaultResourceProvider,
                    useExisting: finalConfig.defaultResourceProvider,
                },
                {
                    provide: PermissionService,
                    useExisting: CachedPermissionService
                },
            ],
        };
    }
    constructor(http) {
        if (!http) {
            throw new Error('You need to import the HttpClientModule in your AppModule! \n' +
                'See also https://github.com/angular/angular/issues/20575');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerModule, deps: [{ token: i1.HttpClient, optional: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerModule, declarations: [IsAllowedAnyImpurePipe,
            IsAllowedEveryImpurePipe,
            IsAllowedNoneImpurePipe,
            IsAllowedImpurePipe,
            IsNotAllowedImpurePipe,
            IsAllowedAnyDirective,
            IsAllowedDirective,
            IsAllowedEveryDirective,
            IsNotAllowedDirective], imports: [CommonModule], exports: [IsAllowedAnyImpurePipe,
            IsAllowedEveryImpurePipe,
            IsAllowedNoneImpurePipe,
            IsAllowedImpurePipe,
            IsNotAllowedImpurePipe,
            IsAllowedAnyDirective,
            IsAllowedDirective,
            IsAllowedEveryDirective,
            IsNotAllowedDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...publicApi],
                    exports: [...publicApi],
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient, decorators: [{
                    type: Optional
                }] }]; } });

function castToObservable(input) {
    return isObservable(input) || isPromise(input)
        ? from(input)
        : of(input);
}

/**
 * Checks authorization against the specified resource.
 *
 * If `resourceFactory` is not provided, the default resource (see {@link DefaultResourceProvider}) will be used.
 *
 * @example
 * // #1 Default resource
 * RouterModule.forRoot([
 *   {
 *     path: '/dashboard',
 *     canActivate: [isAuthorizedGuard('domain:permission')]
 *   }
 * ])
 * // #2 Custom resource
 * RouterModule.forRoot([
 *   {
 *     path: '/dashboard',
 *     canActivate: [
 *       isAuthorizedGuard('domain:permission', {
 *         resourceFactory: (route: ActivatedRouteSnapshot) => ({ type: 'RESOURCE_TYPE', id: route.params['id'] }),
 *       }),
 *     ]
 *   }
 * ])
 * // #3 Custom resource from a service
 * RouterModule.forRoot([
 *   {
 *     path: '/dashboard',
 *     canActivate: [
 *       isAuthorizedGuard('domain:permission', {
 *          resourceFactory: (route: ActivatedRouteSnapshot) => inject(ResourceService).getResource(route.params['id'])
 *      })
 *     ]
 *   }
 * ])
 */
const isAuthorizedGuard = (permission, options = {}) => {
    return (activatedRoute, state) => {
        const injector = inject(EnvironmentInjector);
        const authorizationService = inject(AuthorizationService);
        const onForbidden = options.onForbidden ?? inject(DEFAULT_ON_FORBIDDEN_CALLBACK);
        let resource$ = options.resourceFactory?.(activatedRoute, state);
        resource$ ??= inject(DefaultResourceProvider).getDefaultResource();
        resource$ = castToObservable(resource$);
        const isAllowed$ = authorizationService.isAllowed(permission, resource$);
        return combineLatest([resource$, isAllowed$]).pipe(take(1), switchMap$1(([resource, authorized]) => {
            if (authorized) {
                return of(true);
            }
            else {
                return runInInjectionContext(injector, () => castToObservable(onForbidden(activatedRoute, state))).pipe(take(1), tap((result) => {
                    if (result === false) {
                        console.warn(`ForbiddenRoute: Transition to "${state.url}" has been blocked.`
                            + ` Current user does not have the "${permission}" permission`
                            + ` for the "${resource.type}:${resource.id}" resource.`);
                    }
                }));
            }
        }));
    };
};

/**
 * Checks authorization against the specified resource.
 *
 * If `resourceFactory` is not provided, the default resource (see {@link DefaultResourceProvider}) will be used.
 *
 * @example
 * // #1 Default resource
 * RouterModule.forRoot([
 *   {
 *     path: '/dashboard',
 *     canActivate: [isAuthorizedAnyGuard(['domain:permission', 'domain:permission2'])]
 *   }
 * ])
 * // #2 Custom resource
 * RouterModule.forRoot([
 *   {
 *     path: '/dashboard',
 *     canActivate: [
 *       isAuthorizedAnyGuard(['domain:permission', 'domain:permission2'], {
 *         resourceFactory: (route: ActivatedRouteSnapshot) => ({ type: 'RESOURCE_TYPE', id: route.params['id'] }),
 *       }),
 *     ]
 *   }
 * ])
 * // #3 Custom resource from a service
 * RouterModule.forRoot([
 *   {
 *     path: '/dashboard',
 *     canActivate: [
 *       isAuthorizedAnyGuard(['domain:permission', 'domain:permission2'], {
 *          resourceFactory: (route: ActivatedRouteSnapshot) => inject(ResourceService).getResource(route.params['id'])
 *      })
 *     ]
 *   }
 * ])
 */
const isAuthorizedAnyGuard = (permissions, options = {}) => {
    return (activatedRoute, state) => {
        const injector = inject(EnvironmentInjector);
        const authorizationService = inject(AuthorizationService);
        const onForbidden = options.onForbidden ?? inject(DEFAULT_ON_FORBIDDEN_CALLBACK);
        let resource$ = options.resourceFactory?.(activatedRoute, state);
        resource$ ??= inject(DefaultResourceProvider).getDefaultResource();
        resource$ = castToObservable(resource$);
        const isAllowed$ = authorizationService.isAllowedAny(permissions, resource$);
        return combineLatest([resource$, isAllowed$]).pipe(take(1), switchMap$1(([resource, authorized]) => {
            if (authorized) {
                return of(true);
            }
            else {
                return runInInjectionContext(injector, () => castToObservable(onForbidden(activatedRoute, state))).pipe(take(1), tap((result) => {
                    if (result === false) {
                        console.warn(`ForbiddenRoute: Transition to "${state.url}" has been blocked.`
                            + ` Current user does not have any of "${permissions.join(', ')}" permissions`
                            + ` for the "${resource.type}:${resource.id}" resource.`);
                    }
                }));
            }
        }));
    };
};

const MOCK_RESOURCE = { id: -1, type: 'MOCK' };
class MockPermissionService {
    constructor(defaultResourceProvider) {
        this.defaultResourceProvider = defaultResourceProvider;
        this.update$ = new ReplaySubject(1);
        this.permissionMap = new Map();
        this.defaultPermissions = null;
        this.defaultResource$ = new BehaviorSubject(MOCK_RESOURCE);
        this.getPermissions = (resource, domain) => {
            return this.update$.pipe(combineLatestWith(this.defaultResource$), 
            // If queried resource is the default resource, add default permissions
            map(([_, defaultResource]) => {
                let permissions = this.forResource(resource).permissions;
                if (Array.isArray(this.defaultPermissions)
                    && defaultResource.id === resource.id
                    && defaultResource.type === resource.type) {
                    permissions = [
                        ...this.defaultPermissions,
                        ...permissions ?? [],
                    ];
                }
                return permissions;
            }), filter((permissions) => Array.isArray(permissions)), map((permissions) => { return permissions.filter((p) => splitPermission(p)[0] === domain); }));
        };
        // Keep this.defaultResource$ up to date, starting with MOCK_RESOURCE
        defaultResourceProvider.getDefaultResource().subscribe(this.defaultResource$);
    }
    set permissions(permissions) {
        this.defaultPermissions = permissions;
        this.update$.next(null);
    }
    forResource(resource) {
        const permissionMap = this.permissionMap;
        const getKey = (resource) => `${resource.type}:${resource.id}`;
        const update$ = this.update$;
        return {
            get permissions() {
                return permissionMap.get(getKey(resource)) ?? null;
            },
            set permissions(value) {
                permissionMap.set(getKey(resource), value);
                update$.next(null);
            },
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MockPermissionService, deps: [{ token: DefaultResourceProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MockPermissionService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MockPermissionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DefaultResourceProvider }]; } });

/**
 * @deprecated use `MockPermissionService` and `AuthorizationService` instead
 */
class ResourceAuthorizationServiceStub {
    constructor(permissionService, authorizationService) {
        this.permissionService = permissionService;
        this.authorizationService = authorizationService;
    }
    getPermissionList(domain) {
        const permissions = this.permissionService.permissions?.filter((p) => splitPermission(p)[0] === domain);
        return permissions ? of([]) : EMPTY;
    }
    hasPermission(permission) {
        return this.authorizationService.isAllowed(permission);
    }
    hasAnyPermission(permissions) {
        return this.authorizationService.isAllowedAny(permissions);
    }
    hasEveryPermission(permissions) {
        return this.authorizationService.isAllowedEvery(permissions);
    }
    set permissions(permissions) {
        this.permissionService.permissions = permissions;
    }
}
/**
 * @deprecated use `AuthorizationService` instead
 */
class AuthorizationServiceResolverStub {
    constructor() {
        this.service = new ResourceAuthorizationServiceStub(inject(MockPermissionService), inject(AuthorizationService));
        this.resolve = () => this.service;
        this.getDefaultService = () => of(this.service);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationServiceResolverStub, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationServiceResolverStub }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizationServiceResolverStub, decorators: [{
            type: Injectable
        }] });
class DefaultResourceStub {
    constructor() {
        this.getDefaultResource = () => new BehaviorSubject(TEST_ORGANIZATION_RESOURCE);
    }
}
const TEST_ORGANIZATION_RESOURCE = {
    type: 'ORGANIZATION_PHONEBOOK_ID',
    id: 1,
};
const TEST_ARRAY_RESOURCE = {
    type: 'FLASH_ARRAY_ARRAY_ID',
    id: '24',
};
class MockAuthorizationProvider {
    constructor() {
        this.listAuthorizedPermissionsScoped = () => EMPTY;
    }
}
class AuthorizerTestingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerTestingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerTestingModule, imports: [AuthorizerModule, HttpClientTestingModule], exports: [AuthorizerModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerTestingModule, providers: [
            AuthorizationService,
            MockPermissionService,
            {
                provide: AuthorizationServiceResolver,
                useClass: AuthorizationServiceResolverStub,
            },
            { provide: DefaultResourceProvider, useClass: DefaultResourceStub },
            {
                provide: PermissionService,
                useExisting: MockPermissionService,
            },
            {
                provide: CachedPermissionService,
                useExisting: MockPermissionService,
            },
            {
                provide: AuthzNGService,
                useClass: MockAuthorizationProvider,
            },
        ], imports: [AuthorizerModule, HttpClientTestingModule, AuthorizerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorizerTestingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [AuthorizerModule, HttpClientTestingModule],
                    exports: [AuthorizerModule],
                    providers: [
                        AuthorizationService,
                        MockPermissionService,
                        {
                            provide: AuthorizationServiceResolver,
                            useClass: AuthorizationServiceResolverStub,
                        },
                        { provide: DefaultResourceProvider, useClass: DefaultResourceStub },
                        {
                            provide: PermissionService,
                            useExisting: MockPermissionService,
                        },
                        {
                            provide: CachedPermissionService,
                            useExisting: MockPermissionService,
                        },
                        {
                            provide: AuthzNGService,
                            useClass: MockAuthorizationProvider,
                        },
                    ],
                }]
        }] });

/*
 * Public API Surface of authorizer
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthorizationService, AuthorizationServiceResolver, AuthorizationServiceResolverStub, AuthorizerModule, AuthorizerTestingModule, BASE_PATH, CachedPermissionService, DEFAULT_ON_FORBIDDEN_CALLBACK, DefaultResourceProvider, DefaultResourceStub, IsAllowedAnyDirective, IsAllowedAnyImpurePipe, IsAllowedDirective, IsAllowedEveryDirective, IsAllowedEveryImpurePipe, IsAllowedImpurePipe, IsAllowedNoneImpurePipe, IsNotAllowedDirective, IsNotAllowedImpurePipe, MOCK_RESOURCE, MockAuthorizationProvider, MockPermissionService, PermissionService, ResourceAuthorizationServiceStub, TEST_ARRAY_RESOURCE, TEST_ORGANIZATION_RESOURCE, castToObservable, isAuthorizedAnyGuard, isAuthorizedGuard };
